import { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Create a very simple context for authentication
const AuthContext = createContext();

// API URL - use environment variable or fallback
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:5000/api' 
    : 'https://app-backends.vercel.app/api');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set auth token in axios defaults
  const setAuthToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Load user data
  const loadUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
      
      // Set the token in headers
      setAuthToken(token);
      
      // Get user data
      const res = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.data && res.data.data) {
        setUser(res.data.data);
        setIsAuthenticated(true);
      } else {
        // Invalid response
        console.error('Invalid user data response:', res.data);
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Error loading user:', err);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
      setError('Authentication failed. Please log in again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load user on first run and token changes
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Login user
  const login = async (email, password) => {
    try {
      // Clear previous errors
      setError(null);
      
      // Make login request
      const res = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      // Check for token
      if (res.data && res.data.token) {
        // Set token in localStorage and axios headers
        setAuthToken(res.data.token);
        
        // Load user data
        await loadUser();
        return true;
      } else {
        setError('Invalid response from server');
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.response) {
        setError(err.response.data?.error || 'Invalid credentials');
      } else {
        setError('Network error. Please check your connection.');
      }
      
      return false;
    }
  };

  // Logout
  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Clear errors
  const clearErrors = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        login,
        logout,
        clearErrors
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 